import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { SalutationsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsGeneralSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as SalutationActions from "../../../actions/salutations";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
} from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { LookupsLayout } from "../../index";

import "./SalutationsPage.scss";

interface SalutationsPageProps {
    formFilter: IFormFilter;
    salutation: Dtos.Salutation[];
    loadingSalutations: boolean;
    loadSalutationsSuccess: boolean;
    loadSalutationsFailure: boolean;
    loadSalutationsErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearSalutations: SalutationActions.ISalutationClearActionCreator;
    loadSalutations: SalutationActions.ISalutationLoadActionCreator;
    filter: SalutationActions.ISalutationFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const SalutationsPage: FC<SalutationsPageProps> = ({
    formFilter,
    salutation,
    loadingSalutations,
    loadSalutationsSuccess,
    loadSalutationsFailure,
    loadSalutationsErrorCode,
    permissions,
    clearSalutations,
    loadSalutations,
    filter,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showSalutations = !loadingSalutations && loadSalutationsSuccess;


    // Load saluations
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsGeneralSubMenu(permissions));
            setBreadcrumbs(SalutationsBreadcrumbs());

            loadSalutations();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsGeneralSubMenu,
        permissions,
        setBreadcrumbs,
        SalutationsBreadcrumbs,
        loadSalutations,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadSalutationsFailure) {
            navigate(`/error/${loadSalutationsErrorCode}`);
        }
    }, [loadSalutationsFailure, loadSalutationsErrorCode, navigate]);

    // Cleanup effect to clear the salutations on component unmount
    useEffect(() => {
        return () => {
            clearSalutations();
        };
    }, [clearSalutations]);

    const createTable = useMemo(() => {
        if (!salutation || salutation.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>There are currently no salutations to view.</span>
                    </div>
                </div>
            );
        }

        const showPagination = salutation.length > DEFAULT_PAGE_SIZE;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={salutation}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? DEFAULT_PAGE_SIZE : salutation.length
                    }
                    pageSize={showPagination ? undefined : salutation.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no salutation to display"
                    resizable={false}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string = `/settings/general/salutations/${rowInfo.original.id}`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [salutation, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, navigate]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Salutations</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />

                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.SalutationsCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={"/settings/general/salutations/create"}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showSalutations ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showSalutations ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [formFilter, filter, permissions, showSalutations, createTable]);

    return (
        <LookupsLayout
            loading={loadingSalutations}
            permission={Dtos.Permission.SalutationsView}
        >
            {renderContent}
        </LookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<SalutationsPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        formFilter: state.salutations.formFilter,
        salutation: state.salutations.formFilteredData,
        loadingSalutations:
            state.salutations.loadState &&
            state.salutations.loadState.status == RequestState.Pending,
        loadSalutationsSuccess:
            state.salutations.loadState &&
            state.salutations.loadState.status == RequestState.Success,
        loadSalutationsFailure:
            state.salutations.loadState &&
            state.salutations.loadState.status == RequestState.Failure,
        loadSalutationsErrorCode: state.salutations.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (dispatch): Partial<SalutationsPageProps> => {
    return {
        loadSalutations: bindActionCreators(
            SalutationActions.LoadSalutation,
            dispatch
        ),
        clearSalutations: bindActionCreators(SalutationActions.Clear, dispatch),
        filter: bindActionCreators(SalutationActions.Filter, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(SalutationsPage);
